// Use remote API if explicitly configured. Otherwise default to same-origin
// URLs so requests route through the Vite proxy (vite.config.js proxies /api
// and /ws to the backend). Direct cross-origin fetches from the Electron
// renderer to 127.0.0.1:8000 hang on some macOS setups (Local Network
// permission / socket-pool issues), so the proxy path is the reliable
// default in dev.
const API_BASE = window.SIPHON_API_URL || localStorage.getItem('siphon_api_url') || '';

function _wsUrl() {
  // If API_BASE is absolute, flip http→ws on it. Otherwise use same-origin.
  if (API_BASE && /^https?:/.test(API_BASE)) {
    return API_BASE.replace(/^http/, 'ws') + '/ws';
  }
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${window.location.host}/ws`;
}

class SiphonAPI {
  constructor() {
    this.ws = null;
    this.listeners = new Map();
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this._startedAt = Date.now();
    this._backendReady = false;
  }

  // WebSocket connection for real-time progress
  connectWS() {
    try {
      const wsUrl = _wsUrl();
      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = () => {
        console.log('WebSocket connected');
        this.reconnectAttempts = 0;
      };

      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          this.listeners.forEach((callback) => callback(data));
        } catch (e) {
          console.error('Failed to parse WebSocket message:', e);
        }
      };

      this.ws.onerror = (error) => {
        console.error('WebSocket error:', error);
      };

      this.ws.onclose = () => {
        console.log('WebSocket closed, attempting to reconnect...');
        // Auto-reconnect after 3s
        if (this.reconnectAttempts < this.maxReconnectAttempts) {
          this.reconnectAttempts++;
          setTimeout(() => this.connectWS(), 3000);
        }
      };
    } catch (error) {
      console.error('Failed to create WebSocket:', error);
    }
  }

  onJobUpdate(id, callback) {
    this.listeners.set(id, callback);
    return () => this.listeners.delete(id);
  }

  cancelJob(jobId) {
    // Use REST endpoint (reliable) + WebSocket (fast) — belt and suspenders.
    // Return the fetch promise so callers can await it.
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(`cancel:${jobId}`);
    }
    return this.fetch(`/api/jobs/${jobId}/cancel`, { method: 'POST' });
  }

  // ---- Auth helpers ----
  getToken() {
    return localStorage.getItem('siphon_jwt');
  }
  setTokens({ access_token, refresh_token }) {
    if (access_token) localStorage.setItem('siphon_jwt', access_token);
    if (refresh_token) localStorage.setItem('siphon_refresh', refresh_token);
  }
  clearTokens() {
    localStorage.removeItem('siphon_jwt');
    localStorage.removeItem('siphon_refresh');
  }

  // Generic fetch wrapper — silently retries connection errors during startup
  async fetch(path, options = {}, retries = 3) {
    const isStartup = !this._backendReady;
    const token = this.getToken();
    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        const resp = await fetch(`${API_BASE}${path}`, {
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
            ...options.headers,
          },
          ...options,
        });
        // 401 -> let the caller bubble it so AuthGate can redirect to login
        if (resp.status === 401) {
          this.clearTokens();
          const err = new Error('Unauthorized');
          err.status = 401;
          throw err;
        }
        if (!resp.ok) {
          const errorText = await resp.text();
          throw new Error(`API error: ${resp.status} - ${errorText}`);
        }
        this._backendReady = true;
        return resp.json();
      } catch (err) {
        const isConnectionError = err.message === 'Failed to fetch' || err.name === 'TypeError';
        if (isConnectionError && attempt < retries) {
          await new Promise(r => setTimeout(r, 1500 * (attempt + 1)));
          continue;
        }
        // During first 10 seconds of app life, swallow connection errors and return empty data
        if (isConnectionError && (Date.now() - this._startedAt) < 10000) {
          console.log(`[Siphon] Suppressed startup fetch error for ${path}`);
          return Array.isArray(options._default) ? [] : options._default || {};
        }
        throw err;
      }
    }
  }

  // Authenticated file download. Fetches `path` through API_BASE (so it goes
  // via the Vite proxy / correct host in prod) WITH the Authorization header,
  // reads the response as a blob, and triggers a client-side download. Never
  // use a bare <a href> for authed endpoints — those can't send the Bearer
  // token and hardcode a host.
  async downloadFile(path, { filename = 'download', method = 'GET', body = null } = {}) {
    const token = this.getToken();
    const resp = await fetch(`${API_BASE}${path}`, {
      method,
      headers: {
        ...(body ? { 'Content-Type': 'application/json' } : {}),
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      },
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
    if (resp.status === 401) {
      this.clearTokens();
      const err = new Error('Unauthorized');
      err.status = 401;
      throw err;
    }
    if (!resp.ok) {
      const text = await resp.text().catch(() => '');
      throw new Error(`Export failed: ${resp.status}${text ? ' - ' + text : ''}`);
    }
    // Honor a server-provided filename when present.
    const disp = resp.headers.get('Content-Disposition') || '';
    const m = /filename="?([^"]+)"?/.exec(disp);
    if (m) filename = m[1];
    const blob = await resp.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  // Sources
  getSources() {
    return this.fetch('/api/sources');
  }

  createSource(data) {
    return this.fetch('/api/sources', { method: 'POST', body: JSON.stringify(data) });
  }

  deleteSource(id) {
    return this.fetch(`/api/sources/${id}`, { method: 'DELETE' });
  }

  // Leads
  getLeads(params = {}) {
    const qs = new URLSearchParams(params).toString();
    return this.fetch(`/api/leads${qs ? '?' + qs : ''}`);
  }

  deleteLead(id) {
    return this.fetch(`/api/leads/${id}`, { method: 'DELETE' });
  }

  // Upload
  async uploadFile(file, sourceName) {
    const form = new FormData();
    form.append('file', file);
    form.append('source_name', sourceName);
    const token = this.getToken();
    const resp = await fetch(`${API_BASE}/api/import/upload`, {
      method: 'POST',
      headers: {
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      },
      body: form,
    });
    if (resp.status === 401) {
      this.clearTokens();
      const err = new Error('Unauthorized');
      err.status = 401;
      throw err;
    }
    if (!resp.ok) {
      throw new Error(`Upload failed: ${resp.status}`);
    }
    return resp.json();
  }

  // Jobs
  startEnrich(leadIds = null) {
    const opts = { method: 'POST' };
    if (leadIds && leadIds.length > 0) {
      opts.body = JSON.stringify({ lead_ids: leadIds });
    }
    return this.fetch('/api/jobs/enrich', opts);
  }

  startScore(leadIds = null) {
    const opts = { method: 'POST' };
    if (leadIds && leadIds.length > 0) {
      opts.body = JSON.stringify({ lead_ids: leadIds });
    }
    return this.fetch('/api/jobs/score', opts);
  }

  startRoute() {
    return this.fetch('/api/jobs/route', { method: 'POST' });
  }

  startDedup() {
    return this.fetch('/api/jobs/dedup', { method: 'POST' });
  }

  getJobs() {
    return this.fetch('/api/jobs');
  }

  // Buckets
  getBuckets() {
    return this.fetch('/api/buckets');
  }

  createBucket(data) {
    return this.fetch('/api/buckets', { method: 'POST', body: JSON.stringify(data) });
  }

  updateBucket(id, data) {
    return this.fetch(`/api/buckets/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  }

  deleteBucket(id) {
    return this.fetch(`/api/buckets/${id}`, { method: 'DELETE' });
  }

  // Products
  getProducts() {
    return this.fetch('/api/products');
  }

  createProduct(data) {
    return this.fetch('/api/products', { method: 'POST', body: JSON.stringify(data) });
  }

  updateProduct(id, data) {
    return this.fetch(`/api/products/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  }

  deleteProduct(id) {
    return this.fetch(`/api/products/${id}`, { method: 'DELETE' });
  }

  // Analytics
  getSankey() {
    return this.fetch('/api/analytics/sankey');
  }

  getStats() {
    return this.fetch('/api/analytics/stats');
  }

  // Settings
  getSettings() {
    return this.fetch('/api/settings');
  }

  updateSettings(data) {
    return this.fetch('/api/settings', { method: 'PUT', body: JSON.stringify(data) });
  }

  // Profiles
  getProfiles() {
    return this.fetch('/api/profiles');
  }

  createProfile(name) {
    return this.fetch('/api/profiles', {
      method: 'POST',
      body: JSON.stringify({ name })
    });
  }

  switchProfile(profile_id) {
    return this.fetch('/api/profiles/active', {
      method: 'PUT',
      body: JSON.stringify({ profile_id })
    });
  }

  deleteProfile(profile_id) {
    return this.fetch(`/api/profiles/${profile_id}`, { method: 'DELETE' });
  }

  // Company Signals
  getCompanySignals(company_id) {
    return this.fetch(`/api/companies/${company_id}/signals`);
  }

  detectSignals(config = {}) {
    return this.fetch('/api/companies/detect-signals', {
      method: 'POST',
      body: JSON.stringify(config),
    });
  }

  // Company Buckets
  setCompanyBucket(company_id, bucket) {
    return this.fetch(`/api/companies/${company_id}/bucket`, {
      method: 'PUT',
      body: JSON.stringify({ bucket }),
    });
  }

  bulkAssignCompanies(company_ids, user_id, { replace = true } = {}) {
    return this.fetch('/api/companies/bulk/assign', {
      method: 'PUT',
      body: JSON.stringify({ company_ids, user_id, replace }),
    });
  }

  bulkSetCompanyBucket(company_ids, bucket) {
    return this.fetch('/api/companies/bulk/bucket', {
      method: 'PUT',
      body: JSON.stringify({ company_ids, bucket }),
    });
  }

  getBucketSummary() {
    return this.fetch('/api/companies/buckets/summary');
  }

  // Bucket definitions (configurable bucket types)
  getBucketDefinitions() {
    return this.fetch('/api/bucket-definitions');
  }

  createBucketDefinition(def) {
    return this.fetch('/api/bucket-definitions', {
      method: 'POST',
      body: JSON.stringify(def),
    });
  }

  updateBucketDefinition(id, updates) {
    return this.fetch(`/api/bucket-definitions/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  deleteBucketDefinition(id) {
    return this.fetch(`/api/bucket-definitions/${id}`, {
      method: 'DELETE',
    });
  }

  // Intro Network
  getIntroNetwork() {
    return this.fetch('/api/intro-network');
  }

  addIntroMember(member) {
    return this.fetch('/api/intro-network', {
      method: 'POST',
      body: JSON.stringify(member),
    });
  }

  removeIntroMember(id) {
    return this.fetch(`/api/intro-network/${id}`, {
      method: 'DELETE',
    });
  }

  // Bulk Actions
  bulkAction(action, leadIds, params = {}) {
    return this.fetch('/api/leads/bulk-action', {
      method: 'POST',
      body: JSON.stringify({ action, lead_ids: leadIds, ...params })
    });
  }

  bulkEnrich(leadIds) {
    return this.bulkAction('enrich', leadIds);
  }

  bulkScore(leadIds) {
    return this.bulkAction('score', leadIds);
  }

  bulkRoute(leadIds) {
    return this.bulkAction('route', leadIds);
  }

  bulkMoveToBucket(leadIds, bucketId) {
    return this.bulkAction('move_to_bucket', leadIds, { bucket_id: bucketId });
  }

  bulkDelete(leadIds) {
    return this.bulkAction('delete', leadIds);
  }

  bulkReVerifyEmployment(leadIds) {
    return this.bulkAction('reverify_employment', leadIds);
  }

  bulkEnrollInSequence(leadIds, sequenceId) {
    return this.bulkAction('enroll_sequence', leadIds, { sequence_id: sequenceId });
  }

  softEnrich() {
    return this.fetch('/api/leads/soft-enrich', { method: 'POST' });
  }

  // Lead Promotion Pipeline
  promoteAll() {
    return this.fetch('/api/promote', { method: 'POST' });
  }

  promoteConnections() {
    return this.fetch('/api/promote/connections', { method: 'POST' });
  }

  promoteSignals() {
    return this.fetch('/api/promote/signals', { method: 'POST' });
  }

  enrichProfiles(opts = {}) {
    return this.fetch('/api/enrich-profiles', { method: 'POST', body: JSON.stringify(opts) });
  }

  // Companies
  getCompanies(resync = 0) {
    return this.fetch(`/api/companies${resync ? '?resync=1' : ''}`);
  }

  getCompany(company_id) {
    return this.fetch(`/api/companies/${company_id}`);
  }

  // Typeahead used by the deal modal's company picker. Returns slim rows.
  searchCompanies(q, { limit = 8 } = {}) {
    const u = new URLSearchParams({ q: q || '', limit: String(limit) });
    return this.fetch(`/api/companies/search?${u.toString()}`);
  }

  // Quick-create a company from the deal modal. Just name + optional domain
  // (website) / linkedin_url; research agent fills in the rest later.
  createCompanyQuick({ name, website = '', linkedin_url = '' }) {
    return this.fetch('/api/companies', {
      method: 'POST',
      body: JSON.stringify({ name, website, linkedin_url }),
    });
  }

  // Edit a company's identity fields. Pass only what you want to change.
  // Returns 409 if linkedin_url collides with another company.
  updateCompany(company_id, patch) {
    return this.fetch(`/api/companies/${company_id}`, {
      method: 'PUT',
      body: JSON.stringify(patch || {}),
    });
  }

  // Disqualify a company — sets bucket + records the reason. When the
  // chosen reason has auto_dnc=true, also DNCs the company and cascades
  // DNC down to every linked lead.
  disqualifyCompany(company_id, { bucket_id, reason_id, note = '' }) {
    return this.fetch(`/api/companies/${company_id}/disqualify`, {
      method: 'POST',
      body: JSON.stringify({ bucket_id, reason_id, note }),
    });
  }

  // Typeahead for contacts at a given company (used by deal modal).
  leadsAtCompany(company_id, { q = '', limit = 15 } = {}) {
    const u = new URLSearchParams({ company_id, q, limit: String(limit) });
    return this.fetch(`/api/leads/at-company?${u.toString()}`);
  }

  // Quick-create a lead/contact. full_name required; everything else optional.
  createLeadQuick({ full_name, company_id = null, title = '', email = '', linkedin_url = '' }) {
    return this.fetch('/api/leads', {
      method: 'POST',
      body: JSON.stringify({ full_name, company_id, title, email, linkedin_url }),
    });
  }

  researchCompanies(company_ids, { max = 50, force = false } = {}) {
    return this.fetch('/api/companies/research', {
      method: 'POST',
      body: JSON.stringify({ company_ids, max, force })
    });
  }

  scoreCompanies(company_ids, { max = 50, force = false } = {}) {
    return this.fetch('/api/companies/score', {
      method: 'POST',
      body: JSON.stringify({ company_ids, max, force })
    });
  }

  getCompanyResearch(company_id) {
    return this.fetch(`/api/companies/${company_id}/research`);
  }

  researchSingleCompany(company_id, { domain = null } = {}) {
    const body = { company_id };
    if (domain) body.domain = domain;
    return this.fetch('/api/companies/research/single', {
      method: 'POST',
      body: JSON.stringify(body)
    });
  }

  // Deals
  getDeals(params = {}) {
    const qs = new URLSearchParams(params).toString();
    return this.fetch(`/api/deals${qs ? '?' + qs : ''}`);
  }

  createDeal(deal) {
    return this.fetch('/api/deals', { method: 'POST', body: JSON.stringify(deal) });
  }

  updateDeal(id, patch) {
    return this.fetch(`/api/deals/${id}`, { method: 'PUT', body: JSON.stringify(patch) });
  }

  deleteDeal(id) {
    return this.fetch(`/api/deals/${id}`, { method: 'DELETE' });
  }

  getDealPipeline() {
    return this.fetch('/api/deals/pipeline');
  }

  getDealTransitions(dealId) {
    return this.fetch(`/api/deals/${dealId}/transitions`);
  }

  undoTransition(dealId, transitionId) {
    return this.fetch(`/api/deals/${dealId}/transitions/${transitionId}/undo`, { method: 'POST' });
  }

  getDealFunnel({ since, until } = {}) {
    const qs = new URLSearchParams();
    if (since) qs.set('since', since);
    if (until) qs.set('until', until);
    return this.fetch(`/api/reporting/deal-funnel${qs.toString() ? '?' + qs.toString() : ''}`);
  }

  getActivitySummary({ weeks = 12 } = {}) {
    return this.fetch(`/api/reporting/activity-summary?weeks=${weeks}`);
  }

  enrichCompanyContacts(company_ids) {
    return this.fetch('/api/companies/enrich_contacts', {
      method: 'POST',
      body: JSON.stringify({ company_ids })
    });
  }

  async pollJobUntilDone(jobId) {
    // Returns the full job record: { status, error_message, processed, total, ... }
    // so callers can show a meaningful toast or surface backend errors.
    // Previous versions returned just the status string, which meant
    // "completed" silently hid per-item failures inside the handler.
    let data = null;
    while (true) {
      try {
        data = await this.fetch(`/api/jobs/${jobId}`);
        if (data && (data.status === 'completed' || data.status === 'failed' || data.status === 'cancelled')) {
          break;
        }
      } catch (err) {
        // Mild retry: 404 can occur during the brief window before the job
        // row is inserted. Also handles transient network errors.
        console.error('Poll error:', err);
      }
      await new Promise(r => setTimeout(r, 2000));
    }
    return data || { status: 'failed', error_message: 'polling failed' };
  }

  // Deals - Contacts
  getDealContacts(dealId) {
    return this.fetch(`/api/deals/${dealId}/contacts`);
  }

  addDealContact(dealId, leadId, role = '') {
    return this.fetch(`/api/deals/${dealId}/contacts`, {
      method: 'POST',
      body: JSON.stringify({ lead_id: leadId, role })
    });
  }

  removeDealContact(dealId, contactId) {
    return this.fetch(`/api/deals/${dealId}/contacts/${contactId}`, { method: 'DELETE' });
  }

  setPrimaryContact(dealId, contactId) {
    return this.fetch(`/api/deals/${dealId}/contacts/${contactId}`, {
      method: 'PUT',
      body: JSON.stringify({ is_primary: true })
    });
  }

  // Title resolution
  resolveTitles(lead_ids, { max = 100 } = {}) {
    return this.fetch('/api/leads/resolve-titles', { method: 'POST', body: JSON.stringify({ lead_ids, max }) });
  }

  // Credits
  getCredits(refresh = false) {
    return this.fetch(`/api/credits${refresh ? '?refresh=1' : ''}`);
  }

  refreshCredits() {
    return this.fetch('/api/credits/refresh', { method: 'POST' });
  }

  refreshCreditsFor(provider) {
    return this.fetch(`/api/credits/refresh/${provider}`, { method: 'POST' });
  }

  // DNC (Do Not Contact)
  markLeadDnc(lead_id, reason = null) {
    return this.fetch(`/api/leads/${lead_id}/dnc`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  }

  unmarkLeadDnc(lead_id) {
    return this.fetch(`/api/leads/${lead_id}/dnc`, { method: 'DELETE' });
  }

  bulkMarkLeadsDnc(lead_ids, { reason = null, dnc = true } = {}) {
    return this.fetch('/api/leads/dnc-bulk', {
      method: 'POST',
      body: JSON.stringify({ lead_ids, reason, dnc }),
    });
  }

  listDncLeads({ limit = 100, offset = 0, search = '' } = {}) {
    const params = new URLSearchParams({ limit, offset });
    if (search) params.set('search', search);
    return this.fetch(`/api/leads/dnc?${params}`);
  }

  markCompanyDnc(company_id, reason = null) {
    return this.fetch(`/api/companies/${company_id}/dnc`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  }

  unmarkCompanyDnc(company_id) {
    return this.fetch(`/api/companies/${company_id}/dnc`, { method: 'DELETE' });
  }

  listDncCompanies({ limit = 200, offset = 0 } = {}) {
    const params = new URLSearchParams({ limit, offset });
    return this.fetch(`/api/companies/dnc?${params}`);
  }

  // ============== Hosted-version endpoints ==============

  // ---- Auth ----
  async login(email, password) {
    const data = await this.fetch('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    this.setTokens(data);
    return data;
  }
  async register(email, password, name) {
    const data = await this.fetch('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify({ email, password, name }),
    });
    this.setTokens(data);
    return data;
  }
  async logout() {
    const refresh = localStorage.getItem('siphon_refresh');
    try {
      if (refresh) {
        await this.fetch('/api/auth/logout', {
          method: 'POST', body: JSON.stringify({ refresh_token: refresh }),
        });
      }
    } finally {
      this.clearTokens();
    }
  }
  me() { return this.fetch('/api/auth/me'); }
  async refreshSession() {
    const refresh = localStorage.getItem('siphon_refresh');
    if (!refresh) throw new Error('No refresh token');
    const data = await this.fetch('/api/auth/refresh', {
      method: 'POST', body: JSON.stringify({ refresh_token: refresh }),
    });
    this.setTokens(data);
    return data;
  }

  // ---- Users (admin) ----
  listUsers() { return this.fetch('/api/users'); }
  createUser(payload) {
    return this.fetch('/api/users', { method: 'POST', body: JSON.stringify(payload) });
  }
  updateUser(id, patch) {
    return this.fetch(`/api/users/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
  }
  disableUser(id) { return this.fetch(`/api/users/${id}`, { method: 'DELETE' }); }

  // ---- Admin settings ----
  getAdminSettings() { return this.fetch('/api/admin/settings'); }
  updateLLMSettings(payload) {
    return this.fetch('/api/admin/settings/llm', {
      method: 'PUT', body: JSON.stringify(payload),
    });
  }
  // Research model + provider
  updateResearchSettings(payload) {
    return this.fetch('/api/admin/settings/research', {
      method: 'PUT', body: JSON.stringify(payload),
    });
  }
  listSecrets() { return this.fetch('/api/admin/settings/secrets'); }
  setSecret(provider, value) {
    return this.fetch(`/api/admin/settings/secrets/${provider}`, {
      method: 'PUT', body: JSON.stringify({ value }),
    });
  }
  // Daily $ budget caps per provider — see backend/budgets/__init__.py.
  getBudgets() { return this.fetch('/api/admin/settings/budgets'); }
  updateBudgets(budgets) {
    return this.fetch('/api/admin/settings/budgets', {
      method: 'PUT', body: JSON.stringify({ budgets }),
    });
  }
  // Connected agents (admin-only).
  listAdminAgents() { return this.fetch('/api/admin/agents'); }

  // ---- Deal stages (admin-editable) ----
  listDealStages() { return this.fetch('/api/deal-stages'); }
  createDealStage(payload) {
    return this.fetch('/api/admin/deal-stages', { method: 'POST', body: JSON.stringify(payload) });
  }
  updateDealStage(id, patch) {
    return this.fetch(`/api/admin/deal-stages/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
  }
  deleteDealStage(id) {
    return this.fetch(`/api/admin/deal-stages/${id}`, { method: 'DELETE' });
  }

  // ---- Closed-lost reasons ----
  listLostReasons() { return this.fetch('/api/lost-reasons'); }
  createLostReason(payload) {
    return this.fetch('/api/admin/lost-reasons', { method: 'POST', body: JSON.stringify(payload) });
  }
  updateLostReason(id, patch) {
    return this.fetch(`/api/admin/lost-reasons/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
  }
  deleteLostReason(id) {
    return this.fetch(`/api/admin/lost-reasons/${id}`, { method: 'DELETE' });
  }

  // ---- Agent pairing ----
  createPairingCode() {
    return this.fetch('/api/auth/agent-pairing', { method: 'POST' });
  }
  // One-click pair flow: mint a token bound to the current user immediately.
  // The web app then builds `siphon-agent://pair?token=…` and the OS launches
  // the installed desktop app to consume it.
  directPair(payload = {}) {
    return this.fetch('/api/auth/agent-direct-pair', {
      method: 'POST', body: JSON.stringify(payload),
    });
  }
  listMyAgents() { return this.fetch('/api/auth/agents'); }
  revokeAgent(id) {
    return this.fetch(`/api/auth/agents/${id}`, { method: 'DELETE' });
  }
  // ---- Agent installer download (presigned from S3; private bucket) ----
  getAgentInstaller() { return this.fetch('/api/auth/agent-installer'); }
  markAgentDownloaded(arch) {
    return this.fetch('/api/auth/agent-installer/mark-downloaded', {
      method: 'POST', body: JSON.stringify({ arch }),
    });
  }

  // ---- Company assignments ----
  listCompanyAssignments(companyId) {
    return this.fetch(`/api/companies/${companyId}/assignments`);
  }
  addCompanyAssignment(companyId, payload) {
    return this.fetch(`/api/companies/${companyId}/assignments`, {
      method: 'POST', body: JSON.stringify(payload),
    });
  }
  updateCompanyAssignment(companyId, userId, patch) {
    return this.fetch(`/api/companies/${companyId}/assignments/${userId}`, {
      method: 'PATCH', body: JSON.stringify(patch),
    });
  }
  removeCompanyAssignment(companyId, userId) {
    return this.fetch(`/api/companies/${companyId}/assignments/${userId}`, {
      method: 'DELETE',
    });
  }
  companiesForUser(userId) {
    return this.fetch(`/api/users/${userId}/companies`);
  }

  // ---- Agent jobs (for visibility in UI) ----
  enqueueAgentJob(payload) {
    return this.fetch('/api/agent-jobs', { method: 'POST', body: JSON.stringify(payload) });
  }
  listAgentJobs(params = {}) {
    const qs = new URLSearchParams(params).toString();
    return this.fetch(`/api/agent-jobs${qs ? '?' + qs : ''}`);
  }
}

export const api = new SiphonAPI();
